import ContentPage from "../components/ContentPage";

const PrivacyPolicy = () => (
  <ContentPage title="Privacy Policy">
    <h1 className="content-page__title">Privacy Policy</h1>
    <p className="content-page__subtitle">
      How PeerPoetry collects, uses, and protects your information.
    </p>
    <div className="peer-home-page-card p-5 sm:p-8 md:p-10 space-y-6 min-w-0 box-border">
      <p className="text-[#333333] text-base">
        <strong>PeerPoetry</strong> is committed to protecting your privacy. This
        Privacy Policy explains what information we collect, how we use it, and
        your rights regarding your data.
      </p>
      <h2 className="text-xl font-bold mt-6 mb-2">1. Information We Collect</h2>
      <ul className="list-disc list-inside text-[#333333] space-y-1">
        <li><strong>Account information:</strong> username, email address, password (hashed), Google account ID (if using Google login)</li>
        <li><strong>Profile details:</strong> name, bio, profile picture (avatar), cover image (if provided), badge/verification status</li>
        <li><strong>Content:</strong> poems, comments, replies, feedback you submit</li>
        <li><strong>Social data:</strong> followers, following, saved poems (bookmarks), pinned poems</li>
        <li><strong>Messaging data:</strong> private messages, conversations, read receipts</li>
        <li><strong>Notifications:</strong> in-app notifications about likes, comments, follows, mentions</li>
        <li><strong>Usage data:</strong> log data, device/browser info, IP address (for security), last active timestamp</li>
        <li><strong>Authentication data:</strong> session tokens stored in cookies and localStorage</li>
      </ul>
      <h2 className="text-xl font-bold mt-6 mb-2">
        2. How We Use Your Information
      </h2>
      <ul className="list-disc list-inside text-[#333333] space-y-1">
        <li>To create and manage your account</li>
        <li>To display your poems, comments, and profile</li>
        <li>To enable private messaging between users</li>
        <li>To send notifications and important updates</li>
        <li>To provide social features (followers, following, likes)</li>
        <li>To improve and secure our services</li>
        <li>To respond to your feedback or support requests</li>
        <li>To prevent fraud and abuse</li>
        <li>To comply with legal obligations</li>
      </ul>
      <h2 className="text-xl font-bold mt-6 mb-2">
        3. Data Storage & Security
      </h2>
      <p className="text-[#333333] text-base">
        Your data is stored securely on our servers. Passwords are hashed and
        never stored in plain text. We use industry-standard security measures
        to protect your information.
      </p>
      <h2 className="text-xl font-bold mt-6 mb-2">4. Third-Party Services</h2>
      <ul className="list-disc list-inside text-[#333333] space-y-1">
        <li><strong>Google OAuth:</strong> If you choose to sign in with Google, we receive your Google account information (name, email, profile picture) as provided by Google. This is subject to Google's Privacy Policy.</li>
        <li><strong>Email providers:</strong> We use email services to send verification codes, notifications, and important updates.</li>
        <li><strong>Hosting services:</strong> Your data is stored on secure servers managed by our hosting provider.</li>
        <li>All third-party providers are required to protect your information and may only use it for the services we request.</li>
      </ul>
      <h2 className="text-xl font-bold mt-6 mb-2">5. Cookies and Local Storage</h2>
      <p className="text-[#333333] text-base mb-2">
        We use cookies and local storage for:
      </p>
      <ul className="list-disc list-inside text-[#333333] space-y-1 mb-4">
        <li><strong>Authentication:</strong> Session cookies to keep you logged in</li>
        <li><strong>Preferences:</strong> Cookie consent preferences stored in localStorage</li>
        <li><strong>User data:</strong> User information temporarily stored in localStorage for faster page loads</li>
      </ul>
      <p className="text-[#333333] text-base">
        You can control cookies through your browser settings. Note that disabling cookies may affect your ability to use certain features of the platform.
      </p>
      
      <h2 className="text-xl font-bold mt-6 mb-2">6. Data Retention</h2>
      <ul className="list-disc list-inside text-[#333333] space-y-1">
        <li>Your account data is retained while your account is active</li>
        <li>When you delete your account, your personal information is deleted immediately</li>
        <li>Some data may be retained longer if required by law or for legitimate business purposes (e.g., security logs, backups)</li>
        <li>Content you've shared (poems, comments) is deleted when you delete your account</li>
      </ul>

      <h2 className="text-xl font-bold mt-6 mb-2">7. Your Rights</h2>
      <ul className="list-disc list-inside text-[#333333] space-y-1">
        <li><strong>Access:</strong> You can view and update your profile information at any time</li>
        <li><strong>Deletion:</strong> You can request deletion of your account and data by contacting us</li>
        <li><strong>Data Portability:</strong> You can request a copy of your data in a machine-readable format</li>
        <li><strong>Correction:</strong> You can update or correct your personal information through your profile settings</li>
        <li><strong>Objection:</strong> You can object to certain processing of your data</li>
        <li><strong>Withdrawal of Consent:</strong> You can withdraw consent for data processing where applicable</li>
        <li>You can contact us for any privacy-related questions or concerns</li>
      </ul>
      <h2 className="text-xl font-bold mt-6 mb-2">8. Children's Privacy</h2>
      <p className="text-[#333333] text-base">
        Our service is not intended for users under the age of 13 (or the minimum age in your jurisdiction). 
        We do not knowingly collect personal information from children. If you believe we have collected 
        information from a child, please contact us immediately.
      </p>

      <h2 className="text-xl font-bold mt-6 mb-2">9. Data Sharing</h2>
      <p className="text-[#333333] text-base">
        We do not sell your personal information. We may share your data only in the following circumstances:
      </p>
      <ul className="list-disc list-inside text-[#333333] space-y-1">
        <li>With your explicit consent</li>
        <li>To comply with legal obligations or respond to lawful requests</li>
        <li>To protect our rights, privacy, safety, or property</li>
        <li>With service providers who assist in operating our platform (under strict confidentiality agreements)</li>
      </ul>

      <h2 className="text-xl font-bold mt-6 mb-2">10. Contact</h2>
      <p className="text-[#333333] text-base">
        For privacy questions or requests, please use the Feedback page or email
        us at{" "}
        <a
          href="mailto:support@peerpoetry.com"
          className="peer-home-link hover:underline"
        >
          support@peerpoetry.com
        </a>
        .
      </p>
      <p className="text-xs text-[#777777] mt-8 text-center">
        This policy may be updated from time to time. Last updated:{" "}
        {new Date().toLocaleDateString()}
      </p>
    </div>
  </ContentPage>
);

export default PrivacyPolicy;
